import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, Clock, Receipt, Loader2, Star } from 'lucide-react';
import { useState, useEffect, useMemo, useCallback } from 'react';

// iter896 — Pinned reprint list persists per-cashier via localStorage.
// Keeps the pin metadata (id, order_number, label) so we can render
// pinned rows even when they've dropped out of the 10-most-recent
// window. Cap at 5 pins to avoid noise.
const PIN_STORAGE_KEY = 'pos_reprint_pins_v1';
const MAX_PINS = 5;

const loadPins = () => {
  try { return JSON.parse(localStorage.getItem(PIN_STORAGE_KEY) || '[]'); }
  catch { return []; }
};

const savePins = (pins) => {
  try { localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(pins.slice(0, MAX_PINS))); }
  catch { /* private mode / storage full */ }
};

/**
 * iter895 — "Reprint any of the last 10" picker triggered by the
 * POS action-toolbar's Reprint tile. Highlights the very latest
 * order with a "Latest" badge and a big one-tap "Reprint Latest"
 * button at the top so the common case (customer asking for the
 * receipt of the order they just paid) stays fast, while still
 * exposing 9 older orders for the walk-back-later case.
 */
export default function ReprintPickerDialog({
  open, onOpenChange, orders, loading, onReprint, currency = 'SAR',
}) {
  const list = Array.isArray(orders) ? orders : [];
  const latest = list[0];

  // iter896 — Pins per-cashier. On mount, load; every mutation writes
  // back so pins survive across sessions.
  const [pins, setPins] = useState(loadPins);
  useEffect(() => { if (open) setPins(loadPins()); }, [open]);
  const pinnedIds = useMemo(() => new Set(pins.map(p => p.id)), [pins]);

  const togglePin = useCallback((o) => {
    setPins((prev) => {
      const exists = prev.find(p => p.id === o.id);
      let next;
      if (exists) {
        next = prev.filter(p => p.id !== o.id);
      } else {
        const record = {
          id: o.id,
          order_number: o.order_number || (o.id || '').slice(0, 8),
          total: o.total,
          order_type: o.order_type,
          customer_name: o.customer_name,
          pinned_at: new Date().toISOString(),
        };
        next = [record, ...prev].slice(0, MAX_PINS);
      }
      savePins(next);
      return next;
    });
  }, []);

  // Pinned orders are hydrated from the freshest recent-orders payload
  // when possible (so `total` / `created_at` stay live). Otherwise we
  // fall back to the stored snapshot.
  const pinnedRows = useMemo(() => pins.map(p => list.find(o => o.id === p.id) || p), [pins, list]);

  // Older rows = last-10 excluding latest + pinned.
  const olderRows = useMemo(() => {
    return list.slice(1).filter(o => !pinnedIds.has(o.id));
  }, [list, pinnedIds]);

  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      return sameDay
        ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reprint-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-outfit flex items-center gap-2">
            <Printer size={18} className="text-indigo-500" />
            Reprint an Order
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Tap any of the 10 most recent orders to reprint the receipt.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading recent orders…
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No orders yet — complete an order first, then come back to reprint.
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto -mx-1 px-1">
            {latest && (
              <button
                type="button"
                onClick={() => onReprint(latest)}
                data-testid="reprint-picker-latest-btn"
                className="w-full flex items-center gap-3 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white px-4 py-3 shadow-md active:scale-[0.98] transition-all"
              >
                <div className="rounded-full bg-white/20 p-2">
                  <Printer size={18} strokeWidth={2.4} />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold">Reprint Latest</span>
                    <Badge className="bg-white/25 text-white border-0 text-[9px] uppercase tracking-wider">Latest</Badge>
                  </div>
                  <div className="text-xs opacity-90 mt-0.5">
                    #{latest.order_number || (latest.id || '').slice(0, 8)} · {fmtTime(latest.created_at || latest.date)} · {currency} {(latest.total || 0).toFixed(2)}
                  </div>
                </div>
              </button>
            )}

            {/* iter896 — Pinned favourites (max 5). Stays even when
                dropped out of the 10-most-recent window so recurring
                orders (staff meals, dog treats, etc.) are one tap. */}
            {pinnedRows.length > 0 && (
              <>
                <div className="pt-1 pb-0.5 flex items-center gap-1.5">
                  <Star size={11} className="text-amber-500 fill-amber-500" />
                  <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">Pinned</div>
                  <Badge variant="outline" className="text-[9px] h-4 px-1.5">{pinnedRows.length}</Badge>
                </div>
                {pinnedRows.map((o, idx) => (
                  <div key={o.id || `pin-${idx}`} className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onReprint(o)}
                      data-testid={`reprint-picker-pin-${idx}`}
                      className="flex-1 flex items-center gap-3 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20 hover:bg-amber-100/60 dark:hover:bg-amber-900/30 px-3 py-2.5 text-left transition-colors"
                    >
                      <div className="rounded-full bg-amber-100 dark:bg-amber-900/50 p-2">
                        <Star size={12} className="text-amber-600 fill-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-sm">
                          <span className="font-semibold truncate">#{o.order_number || (o.id || '').slice(0, 8)}</span>
                          {o.order_type && (
                            <Badge variant="outline" className="text-[9px] uppercase h-4 px-1.5">
                              {o.order_type.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                          {o.created_at && <><Clock size={10} /> {fmtTime(o.created_at || o.date)}<span className="mx-1">·</span></>}
                          <span>{currency} {(o.total || 0).toFixed(2)}</span>
                          {o.customer_name && <><span className="mx-1">·</span><span className="truncate max-w-[10rem]">{o.customer_name}</span></>}
                        </div>
                      </div>
                      <Printer size={14} className="text-muted-foreground/50" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); togglePin(o); }}
                      data-testid={`reprint-picker-unpin-${idx}`}
                      className="rounded-lg p-2 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                      title="Unpin"
                    >
                      <Star size={14} className="text-amber-500 fill-amber-500" />
                    </button>
                  </div>
                ))}
              </>
            )}

            {olderRows.length > 0 && (
              <div className="pt-1 pb-0.5">
                <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground font-semibold px-1">
                  Older orders
                </div>
              </div>
            )}

            {olderRows.map((o, idx) => (
              <div key={o.id || idx} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onReprint(o)}
                  data-testid={`reprint-picker-row-${idx}`}
                  className="flex-1 flex items-center gap-3 rounded-lg border border-transparent hover:border-indigo-200 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/20 px-3 py-2.5 text-left transition-colors"
                >
                  <div className="rounded-full bg-muted p-2">
                    <Receipt size={14} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm">
                      <span className="font-semibold truncate">#{o.order_number || (o.id || '').slice(0, 8)}</span>
                      {o.order_type && (
                        <Badge variant="outline" className="text-[9px] uppercase h-4 px-1.5">
                          {o.order_type.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Clock size={10} /> {fmtTime(o.created_at || o.date)}
                      <span className="mx-1">·</span>
                      <span>{currency} {(o.total || 0).toFixed(2)}</span>
                      {o.customer_name && <><span className="mx-1">·</span><span className="truncate max-w-[10rem]">{o.customer_name}</span></>}
                    </div>
                  </div>
                  <Printer size={14} className="text-muted-foreground/50" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); togglePin(o); }}
                  data-testid={`reprint-picker-pin-toggle-${idx}`}
                  className="rounded-lg p-2 hover:bg-amber-50 dark:hover:bg-amber-900/40 transition-colors"
                  title={pinnedIds.has(o.id) ? 'Unpin' : 'Pin to top'}
                >
                  <Star size={14} className={pinnedIds.has(o.id) ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/40 hover:text-amber-400'} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="reprint-picker-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
